import { useEffect, useState } from 'react';
import { NavLink, Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SHORT_DISCLAIMER, greetingName } from '@titrate/shared';
import { useLogout, useMe, useMeta } from '../lib/queries';
import { ThemeToggle } from './ThemeToggle';

function navClass({ isActive }: { isActive: boolean }) {
  return `rounded-lg px-3 py-2 text-sm font-medium transition ${
    isActive ? 'bg-brand-50 dark:bg-brand-500/15 text-brand-800 dark:text-brand-300' : 'text-muted hover:bg-raised hover:text-ink'
  }`;
}

/**
 * The same links as the desktop bar, but sized for a thumb: full-width rows
 * with a 44px minimum height rather than the dense inline pills.
 */
function mobileNavClass({ isActive }: { isActive: boolean }) {
  return `flex min-h-11 items-center rounded-lg px-3 text-base font-medium transition ${
    isActive ? 'bg-brand-50 dark:bg-brand-500/15 text-brand-800 dark:text-brand-300' : 'text-muted hover:bg-raised hover:text-ink'
  }`;
}

export function Layout() {
  const { data: me } = useMe();
  const { data: meta } = useMeta();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const user = me?.user ?? null;
  const [menuOpen, setMenuOpen] = useState(false);

  // A route change means the menu has done its job. Without this it stays
  // open over the page the user just asked for.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Escape closes it, as it would any other transient overlay.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const signOut = () => logout.mutate(undefined, { onSuccess: () => navigate('/') });

  return (
    <div className="flex min-h-screen flex-col">
      {/*
        `pt-safe` keeps the bar clear of the status bar when the site is run
        from the home screen, where `viewport-fit=cover` puts the page
        underneath it. The horizontal padding does the same for a landscape
        notch, falling back to the normal 1rem gutter everywhere else.
      */}
      <header className="sticky top-0 z-30 border-b border-line bg-surface/90 pt-safe backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-safe py-3 sm:gap-4">
          <Link to={user ? '/dashboard' : '/'} className="flex shrink-0 items-center gap-2">
            {/* A droplet: titration is adding one drop at a time to the endpoint. */}
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-700 text-white">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M12 2.2c.3 0 .6.15.77.4C14.1 4.5 18.5 10.6 18.5 14a6.5 6.5 0 0 1-13 0c0-3.4 4.4-9.5 5.73-11.4.17-.25.47-.4.77-.4Z" />
              </svg>
            </span>
            <span className="text-base font-bold tracking-tight text-ink">Titrate</span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {user ? (
              <>
                <NavLink to="/dashboard" className={navClass}>Dashboard</NavLink>
                <NavLink to="/practice" className={navClass}>Practice</NavLink>
                <NavLink to="/revision" className={navClass}>Revision plan</NavLink>
                <NavLink to="/history" className={navClass}>History</NavLink>
                {meta?.features.textbookSwap ? (
                  <NavLink to="/marketplace" className={navClass}>Textbook swap</NavLink>
                ) : null}
                {user.isAdmin ? <NavLink to="/admin" className={navClass}>Admin</NavLink> : null}
              </>
            ) : (
              <>
                <NavLink to="/pricing" className={navClass}>Pricing</NavLink>
                <NavLink to="/about" className={navClass}>About the exams</NavLink>
              </>
            )}
          </nav>

          {/*
            Below `md` this collapses to the one action worth a permanent slot
            plus the menu trigger. Everything else — the theme toggle, the
            account link, sign in/out — moves into the sheet, because the full
            desktop set overflows the bar at any common phone width.
          */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <div className="hidden md:block">
              <ThemeToggle />
            </div>
            {user ? (
              <>
                <Link to="/account" className="btn-ghost hidden md:inline-flex">
                  {greetingName(user) ?? user.email}
                </Link>
                <button type="button" className="btn-secondary hidden md:inline-flex" onClick={signOut}>
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost hidden md:inline-flex">Sign in</Link>
                <Link to="/register" className="btn-primary whitespace-nowrap">Get started</Link>
              </>
            )}

            <button
              type="button"
              className="btn-ghost -mr-2 px-2 md:hidden"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div id="mobile-menu" className="border-t border-line bg-surface md:hidden">
            <nav className="mx-auto max-w-6xl space-y-1 px-safe py-3">
              {user ? (
                <>
                  <NavLink to="/dashboard" className={mobileNavClass}>Dashboard</NavLink>
                  <NavLink to="/practice" className={mobileNavClass}>Practice</NavLink>
                  <NavLink to="/revision" className={mobileNavClass}>Revision plan</NavLink>
                  <NavLink to="/history" className={mobileNavClass}>History</NavLink>
                  {meta?.features.textbookSwap ? (
                    <NavLink to="/marketplace" className={mobileNavClass}>Textbook swap</NavLink>
                  ) : null}
                  {user.isAdmin ? <NavLink to="/admin" className={mobileNavClass}>Admin</NavLink> : null}
                  <NavLink to="/account" className={mobileNavClass}>Account</NavLink>
                </>
              ) : (
                <>
                  <NavLink to="/pricing" className={mobileNavClass}>Pricing</NavLink>
                  <NavLink to="/about" className={mobileNavClass}>About the exams</NavLink>
                  <NavLink to="/login" className={mobileNavClass}>Sign in</NavLink>
                </>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
                <ThemeToggle />
                {user ? (
                  <button type="button" className="btn-secondary" onClick={signOut}>
                    Sign out
                  </button>
                ) : null}
              </div>
            </nav>
          </div>
        ) : null}
      </header>

      {user && !user.emailVerified ? <VerifyBanner /> : null}

      <main className="mx-auto w-full max-w-6xl flex-1 px-safe py-6 sm:py-8">
        <Outlet />
      </main>

      <Footer />
    </div>
  );
}

function VerifyBanner() {
  return (
    <div className="border-b border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10">
      <div className="mx-auto max-w-6xl px-safe py-2.5 text-sm text-amber-900 dark:text-amber-200">
        Please confirm your email address to unlock AI feedback reports, revision plans and the AI tutor.{' '}
        <Link to="/account" className="font-semibold underline underline-offset-2">
          Resend the confirmation email
        </Link>
      </div>
    </div>
  );
}

/**
 * The independence disclaimer is a hard requirement and lives here so it appears
 * on every page of the product, signed in or out.
 */
export function Footer() {
  return (
    <footer className="mt-auto border-t border-line bg-surface pb-safe">
      <div className="mx-auto max-w-6xl px-safe py-8">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" className="text-muted hover:text-ink">Terms of Service</Link>
          <Link to="/privacy" className="text-muted hover:text-ink">Privacy Policy</Link>
          <Link to="/refunds" className="text-muted hover:text-ink">Refunds &amp; cancellation</Link>
          <Link to="/about" className="text-muted hover:text-ink">About the exams</Link>
        </div>
        <p className="mt-5 max-w-4xl text-xs leading-relaxed text-muted">{SHORT_DISCLAIMER}</p>
        <p className="mt-2 text-xs text-subtle">
          © {new Date().getFullYear()} Titrate. All practice questions are original content.
        </p>
      </div>
    </footer>
  );
}
